import { Fragment, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { getToken } from '../lib/api';
import { useI18n, type Lang } from '../lib/i18n';

/** Turn **bold** markers from i18n strings into <b> nodes. */
const Rich = ({ text }: { text: string }) => {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
          return <b key={i}>{part.slice(2, -2)}</b>;
        }
        return <Fragment key={i}>{part}</Fragment>;
      })}
    </>
  );
};

export default function HelpPage() {
  const { t, lang, setLang } = useI18n();
  const [copied, setCopied] = useState(false);
  // Public page: guests go to login, not the desk gate (no token → no terminal).
  const backTo = getToken() ? '/' : '/login';
  const shareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/help`
    : 'https://termix.battletoads.top/help';
  const directUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/help-overview.mp4`
    : 'https://termix.battletoads.top/help-overview.mp4';

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt(t.helpVideoShare, shareUrl);
    }
  };

  const langBtn = (code: Lang, label: string): ReactNode => (
    <button
      type="button"
      key={code}
      onClick={() => setLang(code)}
      style={{
        ...chip,
        borderColor: lang === code ? 'var(--gold)' : 'var(--line)',
        color: lang === code ? 'var(--gold)' : 'var(--muted)',
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ height: '100%', overflow: 'auto', maxWidth: 860, margin: '0 auto', padding: '24px 16px 48px', lineHeight: 1.55, boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ fontFamily: 'var(--display)', fontSize: 36, color: 'var(--gold)' }}>{t.helpTitle}</div>
        <Link to={backTo} style={{ color: 'var(--muted)' }}>{getToken() ? t.helpBack : t.helpBackLogin}</Link>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {langBtn('en', t.langEn)}
          {langBtn('ru', t.langRu)}
        </div>
      </div>

      <section style={card}>
        <h2 style={h2}>{t.helpVideoTitle}</h2>
        <p style={{ marginTop: 0, color: 'var(--muted)', fontSize: 14 }}>{t.helpVideoLead}</p>
        <video
          controls
          playsInline
          preload="metadata"
          poster="/help-poster.png"
          style={{
            width: '100%', borderRadius: 10, border: '1px solid var(--line)',
            background: '#0e1218', aspectRatio: '16 / 9',
          }}
        >
          <source src="/help-overview.mp4" type="video/mp4" />
          <track kind="subtitles" src="/help-overview.en.vtt" srcLang="en" label="English" default={lang === 'en'} />
          <track kind="subtitles" src="/help-overview.ru.vtt" srcLang="ru" label="Русский" default={lang === 'ru'} />
        </video>
        <div style={{
          marginTop: 12, padding: '10px 12px', borderRadius: 10,
          border: '1px solid var(--gold)', background: 'rgba(223,184,58,0.07)',
        }}>
          <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.4 }}>{t.helpVideoShareHint}</div>
          <div style={{
            marginTop: 8, fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--gold)',
            wordBreak: 'break-all',
          }}>
            {shareUrl}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
            <button type="button" onClick={() => void copyShare()} style={chip}>
              {copied ? t.helpVideoShareCopied : t.helpVideoShare}
            </button>
            <a href={directUrl} target="_blank" rel="noreferrer" style={{ ...chip, textDecoration: 'none' }}>
              {t.helpVideoDirect}
            </a>
            <Link to="/login" style={{ ...chip, textDecoration: 'none', borderColor: 'var(--gold)', color: 'var(--gold)' }}>
              {t.helpVideoRegister}
            </Link>
          </div>
        </div>
      </section>

      <section style={card}>
        <h2 style={h2}>{t.helpQuickTitle}</h2>
        <ol style={{ margin: 0, paddingLeft: 20 }}>
          {t.helpQuick.map((line) => (
            <li key={line}><Rich text={line} /></li>
          ))}
        </ol>
      </section>

      <section style={card}>
        <h2 style={h2}>{t.helpStratTitle}</h2>
        <p style={{ marginTop: 0, fontSize: 14 }}>{t.helpStratLead}</p>
        <div style={algo}>
          <div style={algoTitle}>{t.helpVolTitle}</div>
          <p style={algoBody}><Rich text={t.helpVolBody} /></p>
        </div>
        <div style={algo}>
          <div style={algoTitle}>{t.helpHideepTitle}</div>
          <p style={algoBody}>{t.helpHideepBody}</p>
        </div>
        <div style={{ ...algo, marginBottom: 0 }}>
          <div style={algoTitle}>{t.helpDcaTitle}</div>
          <p style={algoBody}>{t.helpDcaBody}</p>
        </div>
        <p style={{ marginBottom: 0, marginTop: 12, fontSize: 13, color: 'var(--muted)' }}>
          {t.helpStratFoot}
        </p>
      </section>

      <section style={card}>
        <h2 style={h2}>{t.helpRangeTitle}</h2>
        <ul>
          {t.helpRangeItems.map((line) => (
            <li key={line}><Rich text={line} /></li>
          ))}
        </ul>
      </section>

      <section style={card}>
        <h2 style={h2}>{t.helpChartTitle}</h2>
        <p><Rich text={t.helpChartBody} /></p>
        <p style={{ marginBottom: 0 }}><Rich text={t.helpChartPair} /></p>
      </section>

      <section style={card}>
        <h2 style={h2}>{t.helpDrawTitle}</h2>
        <ul style={{ marginBottom: 0 }}>
          {t.helpDrawItems.map((line) => (
            <li key={line}><Rich text={line} /></li>
          ))}
        </ul>
      </section>

      <section style={card}>
        <h2 style={h2}>{t.helpRiskTitle}</h2>
        <p><Rich text={t.helpRiskBody1} /></p>
        <p><Rich text={t.helpRiskBody2} /></p>
        <p style={{ marginBottom: 0 }}><Rich text={t.helpRiskBody3} /></p>
      </section>

      <section style={card}>
        <h2 style={h2}>{t.helpBottomTitle}</h2>
        <ul style={{ marginBottom: 0 }}>
          {t.helpBottomItems.map((line) => (
            <li key={line}><Rich text={line} /></li>
          ))}
        </ul>
      </section>

      <section style={{ ...card, marginBottom: 0 }}>
        <h2 style={h2}>{t.helpAdminTitle}</h2>
        <p>
          <Rich text={t.helpAdminBody} />{' '}
          <a href="https://github.com/aleksey-34/Termix" style={{ color: 'var(--gold)' }}>aleksey-34/Termix</a>.
        </p>
        <p style={{ marginBottom: 0, color: 'var(--muted)', fontSize: 13 }}>
          {t.helpAdminFoot}
        </p>
      </section>
    </div>
  );
}

const card: CSSProperties = {
  background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 12, padding: 16, marginBottom: 14,
};
const h2: CSSProperties = { margin: '0 0 8px', fontSize: 18, color: 'var(--gold)' };
const algo: CSSProperties = {
  marginBottom: 10, padding: 12, borderRadius: 10,
  border: '1px solid var(--line)', background: 'var(--panel-2)',
};
const algoTitle: CSSProperties = { color: 'var(--gold)', fontWeight: 600, marginBottom: 4 };
const algoBody: CSSProperties = { margin: 0, fontSize: 14, color: 'var(--text)' };
const chip: CSSProperties = {
  padding: '4px 10px', borderRadius: 8, border: '1px solid var(--line)',
  background: 'var(--panel-2)', cursor: 'pointer', fontFamily: 'var(--mono)', fontSize: 12,
};
